import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import type { CoreData, NameEntry, RareData, SexIdx, SexPref, TopData } from "../data/types";
import { createStore, useStore } from "../state/store";
import {
  DEFAULT_FILTERS,
  LEN_MAX,
  decisions,
  filters,
  settings,
  type Character,
  type Filters,
  type Popularity,
  type TrendFilter,
} from "../state/app";
import { exploreBack, goExplore, openName, openOverlay, route } from "../state/router";
import { blurbOf, collectionsFor, type Collection } from "../data/collections";
import { hiddenType, inDeckFor, passes, shownInLists, swipeWith } from "../state/deck";
import { loadRare, loadTop } from "../data/load";
import { nameKey, stripAccents } from "../data/keys";
import { fmt, genderClass, sexIdxFor } from "../data/format";
import { ORIGIN_NOTE, listWords, originCounts, originExamples, originTitle, sexWordPossessive } from "../data/origins";
import { NameRow, VerdictMark } from "./Shortlist";
import { Chips, LengthRange, POP_OPTIONS, Segmented, TREND_OPTIONS, lengthLabel, styleHint, styleOptions } from "./FilterSheet";
import { OriginList, hideShort } from "./pickers";
import { Sheet } from "./Sheet";
import { IconArrow, IconChevron, IconSearch, IconX } from "./icons";
import { TrendsSection } from "./Trends";

// ------------------------------------------------------------------ shared pieces

type SectionId = "top" | "trends" | "search" | "ideas" | "origins" | "az";
const SECTION_LABEL: Record<SectionId, string> = {
  top: "Popular",
  trends: "Trends",
  search: "Search",
  ideas: "Ideas",
  origins: "Origins",
  az: "A–Z",
};

const SECTIONS: SectionId[] = ["top", "trends", "search", "ideas", "origins", "az"];

type ListSort = "popular" | "az" | "rising";

function sortEntries(list: NameEntry[], sort: ListSort, pref: SexPref): NameEntry[] {
  const si = (e: NameEntry) => sexIdxFor(e, pref);
  const out = list.slice();
  if (sort === "az") return out.sort((a, b) => a.name.localeCompare(b.name, "en-GB"));
  if (sort === "rising") return out.sort((a, b) => (b.t[si(b)] ?? -1e9) - (a.t[si(a)] ?? -1e9) || a.name.localeCompare(b.name));
  return out.sort(
    (a, b) => (a.r[si(a)] || 1e9) - (b.r[si(b)] || 1e9) || b.peakCount - a.peakCount || a.name.localeCompare(b.name),
  );
}

/** A random name from a list, the popular ones a little more likely. */
function luckyPick(list: NameEntry[], pref: SexPref): NameEntry | null {
  if (!list.length) return null;
  const w = list.map((e) => Math.sqrt(e.c[sexIdxFor(e, pref)]) + 1);
  let r = Math.random() * w.reduce((a, b) => a + b, 0);
  for (let i = 0; i < list.length; i++) {
    r -= w[i];
    if (r <= 0) return list[i];
  }
  return list[list.length - 1];
}

const scrollTops = new Map<string, number>();

/** Each Explore page keeps its own scroll position, so Back lands where you were. */
function useScrollMemory(key: string): void {
  useLayoutEffect(() => {
    const el = document.getElementById("main");
    if (!el) return;
    el.scrollTop = scrollTops.get(key) ?? 0;
    const save = () => scrollTops.set(key, el.scrollTop);
    el.addEventListener("scroll", save, { passive: true });
    return () => el.removeEventListener("scroll", save);
  }, [key]);
}

const shownCounts = new Map<string, number>();

function PagedRows({ id, list, core, pref }: { id: string; list: NameEntry[]; core: CoreData; pref: SexPref }) {
  const [n, setN] = useState(() => shownCounts.get(id) ?? 100);
  const map = useStore(decisions, (s) => s.map);
  useEffect(() => {
    shownCounts.set(id, n);
  }, [id, n]);
  if (!list.length) return <p class="empty-note">No names here with your current choices.</p>;
  return (
    <>
      <ul class="rows">
        {list.slice(0, n).map((e) => (
          <NameRow key={e.key} e={e} core={core} pref={pref} badge={<VerdictMark d={map[e.key]?.d} />} />
        ))}
      </ul>
      {list.length > n && (
        <button class="btn btn-wide more-btn" onClick={() => setN(n + 200)}>
          Show {fmt(Math.min(200, list.length - n))} more
        </button>
      )}
    </>
  );
}

function BackBar({ label }: { label: string }) {
  return (
    <button class="back-btn" onClick={exploreBack}>
      <IconChevron dir="left" size={20} /> {label}
    </button>
  );
}

function SortSelect({ value, onChange }: { value: ListSort; onChange: (v: ListSort) => void }) {
  return (
    <label class="sort-select">
      <span>Order</span>
      <select value={value} onChange={(ev) => onChange((ev.target as HTMLSelectElement).value as ListSort)}>
        <option value="popular">Most popular</option>
        <option value="az">A to Z</option>
        <option value="rising">Fastest rising</option>
      </select>
    </label>
  );
}

/** Says which kinds of name the lists are leaving out, with a way to change it. */
function HiddenNote() {
  const f = useStore(filters, (s) => s);
  const parts = [...f.hideOrigins.map((o) => `${o} names`), ...f.hide.map(hideShort)];
  if (!parts.length) return null;
  return (
    <p class="hidden-note">
      Leaving out {listWords(parts, 4)}.{" "}
      <button class="link-btn" onClick={() => openOverlay("filters")}>
        Change
      </button>
    </p>
  );
}

function SubTabs({ sections, current }: { sections: SectionId[]; current: SectionId }) {
  const ref = useRef<HTMLElement>(null);
  const [more, setMore] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // on a phone the last tabs are off to the right: start with the current one in view, and
    // fade the edge while there are more to see
    const on = el.querySelector<HTMLElement>(".on");
    if (on && on.offsetLeft + on.offsetWidth > el.clientWidth) el.scrollLeft = on.offsetLeft - 12;
    const check = () => setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    check();
    el.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      el.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);
  return (
    <div class={`subtabs-bar${more ? " more" : ""}`}>
      <nav class="subtabs" aria-label="Explore" ref={ref}>
        {sections.map((s) => (
          <button
            key={s}
            class={s === current ? "on" : ""}
            aria-current={s === current ? "page" : undefined}
            onClick={() => goExplore([s], "replace")}
          >
            {SECTION_LABEL[s]}
          </button>
        ))}
      </nav>
    </div>
  );
}

// ------------------------------------------------------------------ popular: the top 100 for any year

interface TopRow {
  name: string;
  rank: number;
  count: number;
  /** places gained since the year before; null = new to the top 100; undefined = no year before */
  move: number | null | undefined;
}

function topRows(top: TopData, yi: number, si: SexIdx): TopRow[] {
  const lists = si ? top.M : top.F;
  const counts = si ? top.Mc : top.Fc;
  const prevRank = new Map<number, number>();
  if (yi > 0) {
    let r = 0;
    lists[yi - 1].forEach((id, i) => {
      if (i === 0 || counts[yi - 1][i] !== counts[yi - 1][i - 1]) r = i + 1;
      prevRank.set(id, r);
    });
  }
  let r = 0;
  return lists[yi].map((id, i) => {
    // equal counts share a rank
    if (i === 0 || counts[yi][i] !== counts[yi][i - 1]) r = i + 1;
    const pr = prevRank.get(id);
    return { name: top.names[id], rank: r, count: counts[yi][i], move: yi === 0 ? undefined : pr ? pr - r : null };
  });
}

/** a row's change since the year before; the words in it are for screen readers */
function Move({ m }: { m: number | null | undefined }) {
  if (m === undefined) return <span class="tr-move" />;
  if (m === null) return <span class="tr-move new">New</span>;
  if (m === 0)
    return (
      <span class="tr-move flat">
        <span aria-hidden="true">–</span>
        <span class="sr-only">, same place</span>
      </span>
    );
  return (
    <span class={`tr-move ${m > 0 ? "up" : "down"}`}>
      <span class="sr-only">, {m > 0 ? "up" : "down"} </span>
      <IconArrow dir={m > 0 ? "up" : "down"} size={12} />
      {Math.abs(m)}
    </span>
  );
}

/** the year and list last looked at, per country */
const topChoice = new Map<string, { year: number; si: SexIdx }>();

function TopSection({ core }: { core: CoreData }) {
  const pref = useStore(settings, (s) => s.sex);
  const map = useStore(decisions, (s) => s.map);
  const [top, setTop] = useState<TopData | null>(null);
  const [failed, setFailed] = useState(false);
  const saved = topChoice.get(core.country.id);
  const [year, setYear] = useState(saved?.year ?? core.latest);
  const [si, setSi] = useState<SexIdx>(saved?.si ?? (pref === "M" ? 1 : 0));
  useEffect(() => {
    topChoice.set(core.country.id, { year, si });
  }, [core, year, si]);
  useEffect(() => {
    let live = true;
    loadTop()
      .then((t) => live && setTop(t))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [core]);

  const years = top?.years ?? [core.latest];
  const yi = years.includes(year) ? years.indexOf(year) : years.length - 1;
  const shownYear = years[yi];
  const rows = useMemo(() => (top ? topRows(top, yi, si) : []), [top, yi, si]);
  const keys = useMemo(() => rows.map((r) => nameKey(r.name)).filter((k) => core.byKey.has(k)), [rows, core]);
  const regions = core.country.regions.filter((r) => r.years[0] <= shownYear && shownYear <= r.years[1]).map((r) => r.name);

  const swipe = () => {
    if (pref !== "X" && pref !== (si ? "M" : "F")) settings.set({ sex: si ? "M" : "F" });
    swipeWith({ list: { title: `Top 100 ${si ? "boys" : "girls"}, ${shownYear}`, keys, country: core.country.id } });
  };

  return (
    <div class="xpage">
      <div class="top-head">
        <h1 class="xsec-title">Top 100</h1>
        <div class="year-pick">
          <button class="icon-btn" disabled={yi <= 0} aria-label="Year before" onClick={() => setYear(years[yi - 1])}>
            <IconChevron dir="left" size={20} />
          </button>
          <select aria-label="Year" value={shownYear} onChange={(ev) => setYear(Number((ev.target as HTMLSelectElement).value))}>
            {[...years].reverse().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            class="icon-btn"
            disabled={yi >= years.length - 1}
            aria-label="Year after"
            onClick={() => setYear(years[yi + 1])}
          >
            <IconChevron dir="right" size={20} />
          </button>
        </div>
      </div>
      <Segmented<"0" | "1">
        label="Girls or boys"
        value={String(si) as "0" | "1"}
        onChange={(v) => setSi(Number(v) as SexIdx)}
        options={[
          { v: "0", label: "Girls" },
          { v: "1", label: "Boys" },
        ]}
      />
      <p class="top-source">
        {core.country.id === "us"
          ? `Babies born in the US in ${shownYear}, from Social Security card applications.`
          : core.country.id === "other"
            ? `Babies in the UK's and the US's records for ${shownYear}, added together.`
            : `Babies registered in ${listWords(regions, 4)} in ${shownYear}, added together.`}
      </p>
      {!top ? (
        <p class="muted pad">{failed ? (__NATIVE__ ? "Couldn't load the lists. Please try again." : "Couldn't load the lists. Check your connection and try again.") : "Loading…"}</p>
      ) : (
        <>
          <div class="top-cols" aria-hidden="true">
            <span>Rank</span>
            <span>Name</span>
            <span>Babies</span>
            {yi > 0 && <span>Since {years[yi - 1]}</span>}
          </div>
          <ol class={`rows top-list g-${si ? "boy" : "girl"}`} aria-label={`Top 100 ${si ? "boys" : "girls"} in ${shownYear}`}>
            {rows.map((r) => (
              <li key={r.name}>
                <button class="top-row" onClick={() => openName(nameKey(r.name))}>
                  <span class="tr-rank">{r.rank}</span>
                  <span class="tr-name">
                    {r.name}
                    <VerdictMark d={map[nameKey(r.name)]?.d} />
                  </span>
                  <span class="tr-count">
                    {fmt(r.count)}
                    <span class="sr-only"> babies</span>
                  </span>
                  <Move m={r.move} />
                </button>
              </li>
            ))}
          </ol>
          {keys.length > 0 && (
            <button class="btn btn-primary btn-wide more-btn" onClick={swipe}>
              Swipe these {keys.length} names
            </button>
          )}
        </>
      )}
      <RecordFacts core={core} />
    </div>
  );
}

function RecordFacts({ core }: { core: CoreData }) {
  const c = core.country;
  const births = core.births.F.reduce((a, b) => a + b, 0) + core.births.M.reduce((a, b) => a + b, 0);
  return (
    <section class="xsec facts">
      <h2 class="xsec-title">About the records</h2>
      <ul class="fact-list">
        <li>
          <b>{fmt(core.entries.length)}</b> names to swipe: every name given to enough babies since {core.y0} to be more
          than a one-off.
        </li>
        <li>
          <b>{fmt(births)}</b> babies counted from {core.y0} to {core.latest}.
        </li>
        <li>
          {c.regions.length
            ? `${c.regions.map((r) => `${r.name} ${r.years[0]} to ${r.years[1]}`).join(", ")}.`
            : `National lists from ${c.since} to ${core.latest}.`}
        </li>
        <li>
          {c.id === "uk"
            ? "Names given to fewer than 3 babies in a year aren't published. Scotland listed every name until 2023."
            : c.id === "other"
              ? "The UK and US records start together in 1996. Names given to fewer than 3 babies in a year in the UK, or 5 in the US, aren't published."
              : `Names given to fewer than ${c.minListed} babies of either sex in a year aren't published.`}
        </li>
      </ul>
    </section>
  );
}

// ------------------------------------------------------------------ search

type SearchState = Filters & { sex: SexPref };
const SEARCH_KEY = "nameling:search";

function freshSearch(): SearchState {
  const f = filters.get();
  return { ...DEFAULT_FILTERS, hide: f.hide, hideOrigins: f.hideOrigins, sex: settings.get().sex };
}

function readSearch(): SearchState {
  try {
    const raw = sessionStorage.getItem(SEARCH_KEY);
    if (raw) return { ...freshSearch(), ...JSON.parse(raw) };
  } catch {
    /* no session storage */
  }
  return freshSearch();
}

const search = createStore<SearchState>(readSearch());
// the search follows the deck's girls/boys choice whenever that changes
let deckSex = settings.get().sex;
settings.subscribe(() => {
  const sx = settings.get().sex;
  if (sx !== deckSex) {
    deckSex = sx;
    search.set({ sex: sx });
  }
});
search.subscribe(() => {
  try {
    sessionStorage.setItem(SEARCH_KEY, JSON.stringify(search.get()));
  } catch {
    /* ignore */
  }
});

function isBlank(s: SearchState): boolean {
  return (
    !s.nameText.trim() &&
    !s.meaningText.trim() &&
    !s.origins.length &&
    s.pop === "all" &&
    s.trend === "any" &&
    !s.character.length &&
    !s.syl.length &&
    s.lenMin === DEFAULT_FILTERS.lenMin &&
    s.lenMax === DEFAULT_FILTERS.lenMax
  );
}

const STYLE_WORDS: Record<Character, string> = {
  classic: "classic",
  timeless: "timeless",
  vintage: "vintage revivals",
  unisex: "unisex",
  scotland: "big in Scotland",
  ni: "big in Northern Ireland",
};

/** "Girls' names starting with “Ma”, of Hebrew origin, in the top 500" */
function describe(s: SearchState): string {
  const bits: string[] = [];
  const q = s.nameText.trim();
  if (q) bits.push(`${s.nameMode === "contains" ? "containing" : s.nameMode === "ends" ? "ending in" : "starting with"} “${q}”`);
  if (s.meaningText.trim()) bits.push(`with “${s.meaningText.trim()}” in the meaning`);
  if (s.origins.length) bits.push(`of ${listWords(s.origins)} origin`);
  const pop = POP_OPTIONS.find((o) => o.v === s.pop);
  if (s.pop === "known") bits.push("ranked 101 to 1,000");
  else if (s.pop === "uncommon") bits.push("ranked 1,001 to 5,000");
  else if (s.pop === "rare") bits.push("that are rare");
  else if (pop && s.pop !== "all") bits.push(`in the ${pop.label.toLowerCase()}`);
  if (s.trend === "new") bits.push("new in the last five years");
  else if (s.trend !== "any") bits.push(`that are ${s.trend}`);
  if (s.character.length) bits.push(`that are ${listWords(s.character.map((c) => STYLE_WORDS[c]), 6).replace(/ and (?=[^,]*$)/, " or ")}`);
  if (s.lenMin !== DEFAULT_FILTERS.lenMin || s.lenMax !== DEFAULT_FILTERS.lenMax) bits.push(`with ${lengthLabel(s)}`);
  if (s.syl.length)
    bits.push(`with ${listWords(s.syl.slice().sort().map((n) => (n >= 4 ? "4 or more" : String(n))), 6).replace(/ and (?=[^,]*$)/, " or ")} syllable${s.syl.length === 1 && s.syl[0] === 1 ? "" : "s"}`);
  const out = `${sexWordPossessive(s.sex)} ${bits.join(", ")}`.trim();
  const left = [...s.hideOrigins.map((o) => `${o} names`), ...s.hide.map(hideShort)];
  return `${bits.length ? out : `${sexWordPossessive(s.sex)} of every kind`}${left.length ? `, leaving out ${listWords(left, 4)}` : ""}.`;
}

function FormRow({ label, htmlFor, children, stack }: { label: string; htmlFor?: string; children: ComponentChildren; stack?: boolean }) {
  return (
    <div class={`frow ${stack ? "stack" : ""}`}>
      {htmlFor ? (
        <label class="frow-label" for={htmlFor}>
          {label}
        </label>
      ) : (
        <span class="frow-label">{label}</span>
      )}
      <div class="frow-control">{children}</div>
    </div>
  );
}

function SearchSection({ core }: { core: CoreData }) {
  const s = useStore(search, (x) => x);
  const set = (p: Partial<SearchState>) => search.set(p);
  const [picking, setPicking] = useState(false);
  const matches = useMemo(() => core.entries.filter((e) => passes(e, s, s.sex, core)), [s, core]);
  const left = [...s.hideOrigins.map((o) => `${o} names`), ...s.hide.map(hideShort)];
  const blank = isBlank(s);

  const lucky = () => {
    const e = luckyPick(matches, s.sex);
    if (e) openName(e.key);
  };

  return (
    <form
      class="xpage search-form"
      onSubmit={(ev) => {
        ev.preventDefault();
        goExplore(["search", "results"]);
      }}
    >
      <div class="form-head">
        <h1 class="xsec-title">Search</h1>
        {(!blank || s.sex !== settings.get().sex) && (
          <button type="button" class="link-btn" onClick={() => search.set(freshSearch())}>
            Clear
          </button>
        )}
      </div>
      <div class="form-list">
        <FormRow label="Name" htmlFor="search-name" stack>
          <input
            id="search-name"
            type="search"
            class="text-input"
            value={s.nameText}
            placeholder="Letters in the name"
            autocomplete="off"
            autocapitalize="words"
            spellcheck={false}
            enterkeyhint="search"
            onInput={(ev) => set({ nameText: (ev.target as HTMLInputElement).value })}
          />
          <Segmented<SearchState["nameMode"]>
            label="Where in the name"
            value={s.nameMode}
            onChange={(v) => set({ nameMode: v })}
            options={[
              { v: "starts", label: "Starts with" },
              { v: "contains", label: "Contains" },
              { v: "ends", label: "Ends with" },
            ]}
          />
        </FormRow>
        <FormRow label="Meaning" htmlFor="search-meaning" stack>
          <input
            id="search-meaning"
            type="search"
            class="text-input"
            value={s.meaningText}
            placeholder="A word, like light or brave"
            autocomplete="off"
            spellcheck={false}
            enterkeyhint="search"
            onInput={(ev) => set({ meaningText: (ev.target as HTMLInputElement).value })}
          />
        </FormRow>
        <FormRow label="For">
          <Segmented<SexPref>
            label="Names for"
            value={s.sex}
            onChange={(v) => set({ sex: v })}
            options={[
              { v: "F", label: "Girl" },
              { v: "M", label: "Boy" },
              { v: "X", label: "Either" },
            ]}
          />
        </FormRow>
        <button type="button" class="frow frow-btn" onClick={() => setPicking(true)}>
          <span class="frow-label">Origin</span>
          <span class="frow-value">
            {s.origins.length ? listWords(s.origins) : s.hideOrigins.length ? `Any but ${listWords(s.hideOrigins)}` : "Any"}
          </span>
          <IconChevron dir="right" size={18} />
        </button>
        <label class="frow">
              <span class="frow-label">Popularity</span>
              <select
                class="frow-select"
                value={s.pop}
                onChange={(ev) => set({ pop: (ev.target as HTMLSelectElement).value as Popularity })}
              >
                {POP_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.v === "known" ? "Not too common (101 to 1,000)" : o.v === "uncommon" ? "Uncommon (1,001 to 5,000)" : o.label}
                  </option>
                ))}
              </select>
            </label>
            <label class="frow">
              <span class="frow-label">Trend</span>
              <select
                class="frow-select"
                value={s.trend}
                onChange={(ev) => set({ trend: (ev.target as HTMLSelectElement).value as TrendFilter })}
              >
                {TREND_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.v === "new" ? "New in five years" : o.label}
                  </option>
                ))}
              </select>
            </label>
            <FormRow label="Style" stack>
              <Chips<Character>
                label="Style"
                multi
                value={s.character}
                onChange={(v) => set({ character: v })}
                options={styleOptions(core)}
              />
              {s.character.length > 0 && <p class="fhint">{styleHint(s.character)}</p>}
            </FormRow>
        <FormRow label={`Length: ${lengthLabel(s)}`} stack>
          <LengthRange f={s} set={set} />
        </FormRow>
        <FormRow label="Syllables" stack>
          <Chips<number>
            label="Syllables"
            multi
            value={s.syl}
            onChange={(v) => set({ syl: v })}
            options={[
              { v: 1, label: "1" },
              { v: 2, label: "2" },
              { v: 3, label: "3" },
              { v: 4, label: "4+" },
            ]}
          />
        </FormRow>
      </div>
      {left.length > 0 && (
        <p class="hidden-note">
          Leaving out {listWords(left, 4)}, as in your filters.{" "}
          <button type="button" class="link-btn" onClick={() => set({ hide: [], hideOrigins: [] })}>
            Include them
          </button>
        </p>
      )}
      <div class="form-actions">
        <button type="submit" class="btn btn-primary btn-grow" disabled={!matches.length}>
          {matches.length ? `Show ${fmt(matches.length)} name${matches.length === 1 ? "" : "s"}` : "No names match"}
        </button>
        <button type="button" class="btn" disabled={!matches.length} onClick={lucky}>
          Surprise me
        </button>
      </div>
      <Sheet
        open={picking}
        onClose={() => setPicking(false)}
        title="Origins"
        footer={
          <button class="btn btn-primary btn-wide" onClick={() => setPicking(false)}>
            Done
          </button>
        }
      >
        <OriginList
          core={core}
          sex={s.sex}
          only={s.origins}
          hide={s.hideOrigins}
          onChange={(origins, hideOrigins) => set({ origins, hideOrigins })}
        />
      </Sheet>
    </form>
  );
}

interface RareRow {
  name: string;
  total: number;
  fy: number;
  ly: number;
  g: string;
}

/** Recorded names too rare for the deck, when the search is only about how the name is spelt. */
function useRareMatches(s: SearchState): RareRow[] {
  const q = nameKey(s.nameText);
  const eligible =
    !!q &&
    !s.meaningText.trim() &&
    !s.origins.length &&
    (s.pop === "all" || s.pop === "rare") &&
    s.trend === "any" &&
    !s.character.length &&
    !s.syl.length;
  const [rare, setRare] = useState<RareData | null>(null);
  useEffect(() => {
    if (eligible && !rare) loadRare().then(setRare).catch(() => {});
  }, [eligible]);
  return useMemo(() => {
    if (!eligible || !rare) return [];
    const out: RareRow[] = [];
    for (let i = 0; i < rare.n.length; i++) {
      const name = rare.n[i];
      const k = nameKey(name);
      if (!(s.nameMode === "contains" ? k.includes(q) : s.nameMode === "ends" ? k.endsWith(q) : k.startsWith(q))) continue;
      if (s.sex !== "X" && rare.g[i] !== s.sex) continue;
      const len = stripAccents(name).replace(/[^A-Za-z]/g, "").length;
      if (len < s.lenMin || (s.lenMax < LEN_MAX && len > s.lenMax)) continue;
      if (s.hide.includes("joined") && /[- ]/.test(name)) continue;
      if (s.hide.includes("apostrophe") && name.includes("'")) continue;
      out.push({ name, total: rare.t[i], fy: rare.fy[i], ly: rare.ly[i], g: rare.g[i] });
    }
    return out.sort((a, b) => b.total - a.total).slice(0, 60);
  }, [rare, eligible, q, s]);
}

function SearchResults({ core }: { core: CoreData }) {
  const s = useStore(search, (x) => x);
  const map = useStore(decisions, (x) => x.map);
  const [sort, setSort] = useState<ListSort>("popular");
  const list = useMemo(() => core.entries.filter((e) => passes(e, s, s.sex, core)), [s, core]);
  const sorted = useMemo(() => sortEntries(list, sort, s.sex), [list, sort, s.sex]);
  const rare = useRareMatches(s);

  const swipe = () => {
    if (s.sex !== settings.get().sex) settings.set({ sex: s.sex });
    const { sex: _sex, ...f } = s;
    swipeWith({ ...f, collection: null, list: null, sort: "smart" });
  };

  return (
    <div class="xpage">
      <BackBar label="Search" />
      <div class="page-head">
        <h1 class="view-title">
          {fmt(list.length)} name{list.length === 1 ? "" : "s"}
        </h1>
        <p class="view-sub">{describe(s)}</p>
        <div class="page-actions">
          <button class="btn btn-primary" disabled={!list.length} onClick={swipe}>
            Swipe these
          </button>
          <button
            class="btn"
            disabled={!list.length}
            onClick={() => {
              const e = luckyPick(list, s.sex);
              if (e) openName(e.key);
            }}
          >
            Surprise me
          </button>
        </div>
      </div>
      {list.length > 1 && <SortSelect value={sort} onChange={setSort} />}
      <PagedRows key={`search-${sort}`} id={`search-${sort}`} list={sorted} core={core} pref={s.sex} />
      {rare.length > 0 && (
        <>
          <h2 class="list-head">Rarer names</h2>
          <p class="list-note">Recorded, but too rare to be in the swipe deck.</p>
          <ul class="rows">
            {rare.map((r) => (
              <li class={`name-row g-${r.g === "F" ? "girl" : "boy"}`} key={r.name + r.g}>
                <button class="row-main" onClick={() => openName(nameKey(r.name))}>
                  <span class="row-dot" aria-hidden="true" />
                  <span class="row-text">
                    <span class="row-name">
                      {r.name}
                      <VerdictMark d={map[nameKey(r.name)]?.d} />
                    </span>
                    <span class="row-sub">
                      {fmt(r.total)} {r.g === "F" ? "girls" : "boys"}, {r.fy === r.ly ? r.fy : `${r.fy} to ${r.ly}`}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ ideas: collections

function IdeasSection({ core }: { core: CoreData }) {
  const pref = useStore(settings, (s) => s.sex);
  const f = useStore(filters, (s) => s);
  const cols = useMemo(() => collectionsFor(core).filter((c) => c.id !== "top"), [core]);
  const members = useMemo(
    () =>
      new Map(
        cols.map((c) => [
          c.id,
          core.entries
            .filter((e) => shownInLists(e, f, pref) && c.test(e, sexIdxFor(e, pref), core))
            .sort((a, b) => c.order(a, sexIdxFor(a, pref)) - c.order(b, sexIdxFor(b, pref))),
        ]),
      ),
    [cols, core, pref, f.hide, f.hideOrigins],
  );
  return (
    <div class="xpage">
      <h1 class="xsec-title">Ideas</h1>
      <HiddenNote />
      <div class="col-grid">
        {cols.map((c) => {
          const list = members.get(c.id) ?? [];
          if (!list.length) return null;
          return (
            <button class="col-card" key={c.id} onClick={() => goExplore(["ideas", c.id])}>
              <span class="col-title">{c.title}</span>
              <span class="col-blurb">{blurbOf(c, core)}</span>
              <span class="col-preview">
                {list.slice(0, 3).map((e) => (
                  <span key={e.key} class={`g-${genderClass(e)}`}>
                    {e.name}
                  </span>
                ))}
              </span>
              <span class="col-count">{fmt(list.length)} names</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CollectionPage({ core, col }: { core: CoreData; col: Collection }) {
  const pref = useStore(settings, (s) => s.sex);
  const f = useStore(filters, (s) => s);
  const map = useStore(decisions, (s) => s.map);
  const members = useMemo(
    () =>
      core.entries
        .filter((e) => shownInLists(e, f, pref) && col.test(e, sexIdxFor(e, pref), core))
        .sort((a, b) => col.order(a, sexIdxFor(a, pref)) - col.order(b, sexIdxFor(b, pref))),
    [core, col, pref, f.hide, f.hideOrigins],
  );
  const unseen = members.filter((e) => !map[e.key]).length;
  return (
    <div class="xpage">
      <BackBar label="Ideas" />
      <div class="page-head">
        <h1 class="view-title">{col.title}</h1>
        <p class="view-sub">{blurbOf(col, core)}</p>
        <p class="page-meta">
          {fmt(members.length)} names{pref === "X" ? "" : ` for ${pref === "F" ? "girls" : "boys"}`}, {fmt(unseen)} not
          swiped yet
        </p>
        <div class="page-actions">
          <button class="btn btn-primary" disabled={!unseen} onClick={() => swipeWith({ collection: col.id })}>
            Swipe these
          </button>
        </div>
      </div>
      <HiddenNote />
      <PagedRows key={col.id} id={`ideas-${col.id}`} list={members} core={core} pref={pref} />
    </div>
  );
}

// ------------------------------------------------------------------ origins

function OriginsSection({ core }: { core: CoreData }) {
  const pref = useStore(settings, (s) => s.sex);
  const hidden = useStore(filters, (s) => s.hideOrigins);
  const only = useStore(filters, (s) => s.origins);
  const counts = useMemo(() => originCounts(core, (e) => inDeckFor(e, pref)), [core, pref]);
  const examples = useMemo(() => originExamples(core, (e) => inDeckFor(e, pref), (e) => e.r[sexIdxFor(e, pref)]), [core, pref]);
  const known = counts.reduce((a, [, n]) => a + n, 0);
  const total = useMemo(() => core.entries.filter((e) => inDeckFor(e, pref)).length, [core, pref]);
  return (
    <div class="xpage">
      <h1 class="xsec-title">Origins</h1>
      <p class="xsec-sub">
        Origins for {fmt(known)} of the {fmt(total)} names. The rest don't have a settled origin yet.
      </p>
      <ul class="rows origin-rows">
        {counts.map(([o, n]) => (
          <li key={o}>
            <button class={`origin-row ${hidden.includes(o) ? "is-hidden" : ""}`} onClick={() => goExplore(["origins", o])}>
              <span class="or-text">
                <span class="or-name">{o}</span>
                {examples.has(o) && <span class="or-eg">{examples.get(o)!.join(", ")}</span>}
              </span>
              {hidden.includes(o) && <span class="or-flag">Hidden</span>}
              {only.includes(o) && <span class="or-flag">Only</span>}
              <span class="or-count">{fmt(n)}</span>
              <IconChevron dir="right" size={18} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OriginPage({ core, origin }: { core: CoreData; origin: string }) {
  const pref = useStore(settings, (s) => s.sex);
  const f = useStore(filters, (s) => s);
  const [sort, setSort] = useState<ListSort>("popular");
  const hidden = f.hideOrigins.includes(origin);
  const members = useMemo(
    () => core.entries.filter((e) => e.origin === origin && inDeckFor(e, pref) && !hiddenType(e, f.hide)),
    [core, origin, pref, f.hide],
  );
  const sorted = useMemo(() => sortEntries(members, sort, pref), [members, sort, pref]);
  const toggleHidden = (on: boolean) =>
    filters.set({
      hideOrigins: on ? [...f.hideOrigins, origin] : f.hideOrigins.filter((o) => o !== origin),
      origins: f.origins.filter((o) => o !== origin),
    });
  return (
    <div class="xpage">
      <BackBar label="Origins" />
      <div class="page-head">
        <h1 class="view-title">{originTitle(origin)}</h1>
        {ORIGIN_NOTE[origin] && <p class="view-sub">{ORIGIN_NOTE[origin]}</p>}
        <p class="page-meta">
          {fmt(members.length)} names{pref === "X" ? "" : ` for ${pref === "F" ? "girls" : "boys"}`}
        </p>
        <div class="page-actions">
          <button
            class="btn btn-primary"
            disabled={hidden || !members.length}
            onClick={() => swipeWith({ origins: [origin], hideOrigins: f.hideOrigins.filter((o) => o !== origin) })}
          >
            Swipe these
          </button>
        </div>
        <label class="toggle-row page-toggle">
          <span>
            <span class="toggle-label">Hide {origin} names</span>
            <span class="toggle-hint">Leave them out of your deck and lists</span>
          </span>
          <input type="checkbox" role="switch" checked={hidden} onChange={(ev) => toggleHidden((ev.target as HTMLInputElement).checked)} />
          <span class="switch" aria-hidden="true" />
        </label>
      </div>
      {members.length > 1 && <SortSelect value={sort} onChange={setSort} />}
      <PagedRows key={`${origin}-${sort}`} id={`origin-${origin}-${sort}`} list={sorted} core={core} pref={pref} />
    </div>
  );
}

// ------------------------------------------------------------------ A to Z

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function AzSection({ core }: { core: CoreData }) {
  const pref = useStore(settings, (s) => s.sex);
  const f = useStore(filters, (s) => s);
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const e of core.entries) if (shownInLists(e, f, pref)) m[e.initial] = (m[e.initial] ?? 0) + 1;
    return m;
  }, [core, pref, f.hide, f.hideOrigins]);
  return (
    <div class="xpage">
      <h1 class="xsec-title">A to Z</h1>
      <HiddenNote />
      <div class="az-grid">
        {LETTERS.map((L) => (
          <button key={L} class="az-tile" disabled={!counts[L]} onClick={() => goExplore(["az", L])}>
            <b>{L}</b>
            <small>{fmt(counts[L] ?? 0)}</small>
          </button>
        ))}
      </div>
    </div>
  );
}

function secondLetter(e: NameEntry): string {
  return stripAccents(e.name).replace(/[^A-Za-z]/g, "").charAt(1).toLowerCase();
}

function LetterPage({ core, letter }: { core: CoreData; letter: string }) {
  const pref = useStore(settings, (s) => s.sex);
  const f = useStore(filters, (s) => s);
  const [sort, setSort] = useState<ListSort>("az");
  const [second, setSecond] = useState<string | null>(null);
  const members = useMemo(
    () => core.entries.filter((e) => e.initial === letter && shownInLists(e, f, pref)),
    [core, letter, pref, f.hide, f.hideOrigins],
  );
  const seconds = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of members) {
      const c = secondLetter(e);
      if (c) m.set(c, (m.get(c) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [members]);
  const shown = useMemo(
    () => sortEntries(second ? members.filter((e) => secondLetter(e) === second) : members, sort, pref),
    [members, second, sort, pref],
  );
  return (
    <div class="xpage">
      <BackBar label="A to Z" />
      <div class="page-head az-head">
        <h1 class="view-title az-letter">{letter}</h1>
        <p class="page-meta">
          {fmt(members.length)} names{pref === "X" ? "" : ` for ${pref === "F" ? "girls" : "boys"}`}
        </p>
      </div>
      <HiddenNote />
      {seconds.length > 1 && (
        <div class="chips small az-seconds" role="group" aria-label="Second letter">
          <button class={`chip-btn ${second ? "" : "on"}`} aria-pressed={!second} onClick={() => setSecond(null)}>
            All
          </button>
          {seconds.map(([c, n]) => (
            <button
              key={c}
              class={`chip-btn ${second === c ? "on" : ""}`}
              aria-pressed={second === c}
              aria-label={`${letter}${c}, ${n} names`}
              onClick={() => setSecond(second === c ? null : c)}
            >
              {letter}
              {c}
            </button>
          ))}
        </div>
      )}
      {members.length > 1 && <SortSelect value={sort} onChange={setSort} />}
      <PagedRows key={`${letter}-${second}-${sort}`} id={`az-${letter}-${second}-${sort}`} list={shown} core={core} pref={pref} />
    </div>
  );
}

// ------------------------------------------------------------------ look up a name

interface FoundRare {
  key: string;
  name: string;
  total: number;
  fy: number;
  g: "girl" | "boy" | "unisex";
}

const FIND_MAX = 6;

/** "Ottilie?" Type a name and see it straight away: the quickest way to look up a name someone suggests. */
function QuickFind({ core }: { core: CoreData }) {
  const pref = useStore(settings, (s) => s.sex);
  const map = useStore(decisions, (s) => s.map);
  const [text, setText] = useState("");
  const [rare, setRare] = useState<RareData | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const q = nameKey(text);

  const found = useMemo(() => {
    if (!q) return [];
    const best = (e: NameEntry) => (e.key === q ? 0 : Math.min(e.r[0] || 1e6, e.r[1] || 1e6, 1e6 - e.peakCount));
    const order = (a: NameEntry, b: NameEntry) => best(a) - best(b) || a.name.localeCompare(b.name, "en-GB");
    const out = core.entries.filter((e) => e.key.startsWith(q)).sort(order).slice(0, FIND_MAX);
    if (out.length < FIND_MAX && q.length >= 3)
      out.push(...core.entries.filter((e) => !e.key.startsWith(q) && e.key.includes(q)).sort(order).slice(0, FIND_MAX - out.length));
    return out;
  }, [q, core]);

  // names too rare for the deck, once the deck runs short
  const wantRare = q.length >= 2 && found.length < FIND_MAX;
  useEffect(() => {
    if (wantRare && !rare) loadRare().then(setRare).catch(() => {});
  }, [wantRare]);
  const foundRare = useMemo(() => {
    if (!wantRare || !rare) return [];
    const byKey = new Map<string, FoundRare>();
    for (let i = 0; i < rare.n.length; i++) {
      const key = nameKey(rare.n[i]);
      if (!key.startsWith(q) || core.byKey.has(key)) continue;
      const g = rare.g[i] === "F" ? "girl" : "boy";
      const had = byKey.get(key);
      if (had) {
        had.total += rare.t[i];
        had.fy = Math.min(had.fy, rare.fy[i]);
        if (had.g !== g) had.g = "unisex";
      } else byKey.set(key, { key, name: rare.n[i], total: rare.t[i], fy: rare.fy[i], g });
    }
    return [...byKey.values()]
      .sort((a, b) => (a.key === q ? -1 : b.key === q ? 1 : b.total - a.total))
      .slice(0, FIND_MAX - found.length);
  }, [rare, wantRare, q, found, core]);

  const first = found[0] ?? foundRare[0];
  const count = found.length + foundRare.length;
  const looking = wantRare && !rare && !found.length;

  return (
    <div class="qf" role="search">
      <div class="qf-field">
        <IconSearch size={18} />
        <input
          ref={input}
          type="search"
          aria-label="Look up a name"
          class="qf-input"
          value={text}
          placeholder="Look up a name"
          autocomplete="off"
          autocapitalize="words"
          spellcheck={false}
          enterkeyhint="go"
          onInput={(ev) => setText((ev.target as HTMLInputElement).value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter" && first) {
              ev.preventDefault();
              openName(first.key);
            }
          }}
        />
        {text && (
          <button
            type="button"
            class="qf-clear"
            aria-label="Clear"
            onClick={() => {
              setText("");
              input.current?.focus();
            }}
          >
            <IconX size={16} />
          </button>
        )}
      </div>
      {q && count > 0 && (
        <ul class="rows qf-rows">
          {found.map((e) => (
            <NameRow key={e.key} e={e} core={core} pref={pref} badge={<VerdictMark d={map[e.key]?.d} />} />
          ))}
          {foundRare.map((r) => (
            <li class={`name-row g-${r.g}`} key={r.key}>
              <button class="row-main" onClick={() => openName(r.key)}>
                <span class="row-dot" aria-hidden="true" />
                <span class="row-text">
                  <span class="row-name">
                    {r.name}
                    <VerdictMark d={map[r.key]?.d} />
                  </span>
                  <span class="row-sub">
                    Rare: {fmt(r.total)} {r.total === 1 ? "baby" : "babies"} since {r.fy}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {q && !count && (
        <p class="qf-note">
          {looking
            ? "Looking…"
            : `No name starting “${text.trim()}” is in the records for ${core.country.name}. They only list names given to at least ${core.country.minListed} babies in a year.`}
        </p>
      )}
      <p class="sr-only" aria-live="polite">
        {q && !looking ? (first ? `${count} name${count === 1 ? "" : "s"} found. Press Enter to open ${first.name}.` : "No names found.") : ""}
      </p>
    </div>
  );
}

// ------------------------------------------------------------------ the tab

export function ExploreView({ core }: { core: CoreData }) {
  const path = useStore(route, (r) => r.path);
  const sections = SECTIONS;
  const section: SectionId = sections.includes(path[0] as SectionId) ? (path[0] as SectionId) : sections[0];
  const arg = section === path[0] ? path[1] : undefined;
  useScrollMemory(`${core.country.id}/${section}/${arg ?? ""}`);

  let page: ComponentChildren = null;
  if (arg) {
    if (section === "search" && arg === "results") page = <SearchResults core={core} />;
    else if (section === "ideas") {
      const col = collectionsFor(core).find((c) => c.id === arg);
      if (col) page = <CollectionPage core={core} col={col} />;
    } else if (section === "origins") page = <OriginPage core={core} origin={arg} />;
    else if (section === "az" && /^[A-Z]$/.test(arg)) page = <LetterPage core={core} letter={arg} />;
  }

  return (
    <section class="explore-view" aria-label="Explore names">
      {page ?? (
        <>
          <QuickFind key={core.country.id} core={core} />
          <SubTabs sections={sections} current={section} />
          {section === "top" && <TopSection core={core} />}
          {section === "trends" && (
            <TrendsSection
              core={core}
              onEnding={(ending, si) => {
                search.set({ ...freshSearch(), nameText: ending, nameMode: "ends", sex: si ? "M" : "F" });
                goExplore(["search", "results"], "push");
              }}
            />
          )}
          {section === "search" && <SearchSection core={core} />}
          {section === "ideas" && <IdeasSection core={core} />}
          {section === "origins" && <OriginsSection core={core} />}
          {section === "az" && <AzSection core={core} />}
        </>
      )}
    </section>
  );
}
